import type { User } from '../types';

export const META_PIXEL_ID = '1723274642853616';

type MetaStandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'CompleteRegistration'
  | 'AddPaymentInfo'
  | 'StartTrial'
  | 'Subscribe';

type MetaCustomEvent = 'BannerGenerated';

type MetaEventName = MetaStandardEvent | MetaCustomEvent;

type MetaCustomData = Record<string, string | number | boolean | string[] | null | undefined>;

type Fbq = {
  (command: 'track', eventName: MetaStandardEvent, data?: MetaCustomData, options?: { eventID?: string }): void;
  (command: 'trackCustom', eventName: MetaCustomEvent, data?: MetaCustomData, options?: { eventID?: string }): void;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const ANON_ID_COOKIE = 'tasky_meta_anon_id';
const LAST_EVENT_ID_PREFIX = 'tasky_meta_last_event_id:';
const ONCE_PREFIX = 'tasky_meta_once:';

const PLAN_PRICES: Record<string, number> = {
  personal: 9800,
  team: 30000,
  agency: 50000,
};

const PLAN_CONTENT_NAMES: Record<string, string> = {
  personal: 'tasky_personal_plan',
  team: 'tasky_team_plan',
  agency: 'tasky_agency_plan',
};

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const readCookie = (name: string): string | null => {
  if (!isBrowser()) return null;
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name: string, value: string): void => {
  if (!isBrowser()) return;
  const maxAge = 60 * 60 * 24 * 395; // Meta _fbp と同程度に長期保持
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
};

const createUuid = (): string => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const getOrCreateMetaAnonId = (): string => {
  const existing = readCookie(ANON_ID_COOKIE);
  if (existing) return existing;

  const anonId = createUuid();
  writeCookie(ANON_ID_COOKIE, anonId);
  return anonId;
};

const getIdentityToken = (user?: Pick<User, 'id'> | null): string => {
  if (user?.id) return `u_${user.id}`;
  return `a_${getOrCreateMetaAnonId()}`;
};

const createEventId = (eventName: MetaEventName, user?: Pick<User, 'id'> | null): string => {
  return `${eventName}_${getIdentityToken(user)}_${Date.now()}`;
};

const rememberEventId = (eventName: MetaEventName, eventId: string): void => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(`${LAST_EVENT_ID_PREFIX}${eventName}`, eventId);
  } catch {
    // sessionStorage が利用できない環境では無視する。
  }
};

const hasFiredOnce = (key: string): boolean => {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(`${ONCE_PREFIX}${key}`) === '1';
  } catch {
    return false;
  }
};

const markFiredOnce = (key: string): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(`${ONCE_PREFIX}${key}`, '1');
  } catch {
    // localStorage が利用できない環境では無視する。
  }
};

const RESERVED_EVENT_ID_PREFIX = 'tasky_meta_reserved_event_id:';

/**
 * サーバー側（Conversions API）と同じ event_id でブラウザからも撃つための予約。
 * 登録リクエストを投げる時点で id を決めてサーバーへ渡し、あとで fbq を撃つときに
 * ここで予約した同じ id を使う。両方から届いても Meta 側で1件に丸められる。
 */
export const reserveEventId = (
  eventName: MetaStandardEvent,
  user?: Pick<User, 'id'> | null,
): string => {
  const eventId = createEventId(eventName, user);
  if (isBrowser()) {
    try {
      window.sessionStorage.setItem(`${RESERVED_EVENT_ID_PREFIX}${eventName}`, eventId);
    } catch {
      // sessionStorage が使えない環境では予約なしで進む（重複排除が効かないだけ）
    }
  }
  return eventId;
};

const consumeReservedEventId = (eventName: MetaEventName): string | null => {
  if (!isBrowser()) return null;
  try {
    const key = `${RESERVED_EVENT_ID_PREFIX}${eventName}`;
    const value = window.sessionStorage.getItem(key);
    if (value) window.sessionStorage.removeItem(key);
    return value;
  } catch {
    return null;
  }
};

/**
 * 広告のクリック識別子。CAPI に一緒に送らないと「どの広告クリックの成果か」を
 * サーバー側から結び直せない。_fbc / _fbp は Meta ピクセルが作るクッキー、
 * twclid は LP（lp.tasky-ai.com）が .tasky-ai.com に残した着地情報から拾う。
 */
export const getAdClickIds = (): { fbc?: string; fbp?: string; twclid?: string } => {
  const fbc = readCookie('_fbc') || undefined;
  const fbp = readCookie('_fbp') || undefined;
  let twclid = readCookie('twclid') || undefined;

  if (!twclid) {
    // index.html の早期キャプチャ（と LP 側の AdPixels）が .tasky-ai.com に残す Cookie
    const raw = readCookie('tk_attrib');
    if (raw) {
      try {
        twclid = (JSON.parse(raw) as { twclid?: string }).twclid || undefined;
      } catch {
        // 壊れたCookieは無視する
      }
    }
  }

  return { fbc, fbp, twclid };
};

const trackStandard = (
  eventName: MetaStandardEvent,
  data: MetaCustomData = {},
  user?: Pick<User, 'id'> | null,
): string | null => {
  if (!isBrowser() || !window.fbq) return null;
  const eventID = consumeReservedEventId(eventName) ?? createEventId(eventName, user);
  window.fbq('track', eventName, data, { eventID });
  rememberEventId(eventName, eventID);
  return eventID;
};

const trackCustom = (
  eventName: MetaCustomEvent,
  data: MetaCustomData = {},
  user?: Pick<User, 'id'> | null,
): string | null => {
  if (!isBrowser() || !window.fbq) return null;
  const eventID = createEventId(eventName, user);
  window.fbq('trackCustom', eventName, data, { eventID });
  rememberEventId(eventName, eventID);
  return eventID;
};

export const getMetaPlanPayload = (plan?: string | null): {
  currency: 'JPY';
  value: number;
  content_name: string;
} => {
  const normalizedPlan = (plan || 'personal').toLowerCase();
  return {
    currency: 'JPY',
    value: PLAN_PRICES[normalizedPlan] ?? PLAN_PRICES.personal,
    content_name: PLAN_CONTENT_NAMES[normalizedPlan] ?? PLAN_CONTENT_NAMES.personal,
  };
};

export const trackMetaPageView = (): string | null => {
  if (!isBrowser() || !window.fbq) return null;
  window.fbq('track', 'PageView');
  return null;
};

export const trackMetaSignupView = (): string | null => {
  return trackStandard('ViewContent', {
    content_name: 'tasky_signup',
    content_category: 'signup',
  });
};

export const trackMetaCompleteRegistration = (user?: Pick<User, 'id'> | null): string | null => {
  const onceKey = `CompleteRegistration:${getIdentityToken(user)}`;
  if (hasFiredOnce(onceKey)) return null;
  const eventId = trackStandard('CompleteRegistration', {
    content_name: 'tasky_registration',
    status: true,
  }, user);
  if (eventId) markFiredOnce(onceKey);
  return eventId;
};

export const trackMetaBannerGenerated = (
  params: {
    contentIds?: string[];
    completedCount?: number;
    sessionId?: string | null;
    user?: Pick<User, 'id'> | null;
  } = {},
): string | null => {
  const onceKey = `BannerGenerated:${getIdentityToken(params.user)}`;
  if (hasFiredOnce(onceKey)) return null;

  const eventId = trackCustom('BannerGenerated', {
    content_ids: params.contentIds?.length ? params.contentIds : ['tasky_first_banner'],
    completed_count: params.completedCount,
    session_id: params.sessionId,
  }, params.user);

  if (eventId) markFiredOnce(onceKey);
  return eventId;
};

export const trackMetaAddPaymentInfo = (
  params: {
    plan?: string | null;
    sessionId?: string | null;
    user?: Pick<User, 'id'> | null;
  } = {},
): string | null => {
  const onceKey = `AddPaymentInfo:${params.sessionId || `${getIdentityToken(params.user)}:${params.plan || 'personal'}`}`;
  if (hasFiredOnce(onceKey)) return null;

  const eventId = trackStandard('AddPaymentInfo', {
    ...getMetaPlanPayload(params.plan),
    session_id: params.sessionId,
  }, params.user);

  if (eventId) markFiredOnce(onceKey);
  return eventId;
};

export const trackMetaStartTrial = (
  params: {
    plan?: string | null;
    trialDays?: number;
    sessionId?: string | null;
    user?: Pick<User, 'id'> | null;
  } = {},
): string | null => {
  const onceKey = `StartTrial:${params.sessionId || `${getIdentityToken(params.user)}:${params.plan || 'personal'}`}`;
  if (hasFiredOnce(onceKey)) return null;

  const eventId = trackStandard('StartTrial', {
    ...getMetaPlanPayload(params.plan),
    predicted_ltv: getMetaPlanPayload(params.plan).value,
    trial_days: params.trialDays ?? 7,
    session_id: params.sessionId,
  }, params.user);

  if (eventId) markFiredOnce(onceKey);
  return eventId;
};

export const trackMetaSubscribe = (
  params: {
    plan?: string | null;
    sessionId?: string | null;
    user?: Pick<User, 'id'> | null;
  } = {},
): string | null => {
  const onceKey = `Subscribe:${params.sessionId || `${getIdentityToken(params.user)}:${params.plan || 'personal'}`}`;
  if (hasFiredOnce(onceKey)) return null;

  const eventId = trackStandard('Subscribe', {
    ...getMetaPlanPayload(params.plan),
    session_id: params.sessionId,
  }, params.user);

  if (eventId) markFiredOnce(onceKey);
  return eventId;
};
